import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  TablePagination,
  TextField,
  Typography
} from '@material-ui/core';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import MaterialTable from 'material-table';
import moment from 'moment';

import Header from '../layout/Header';
import NotyHelper from '../api/NotyHelper';
import ApiClient from '../api/ApiClient';

const API_URL = process.env.REACT_APP_API_URL;

const getAccessToken = () => {
  try {
    return JSON.parse(JSON.parse(localStorage.getItem('persist:root')).auth).accessToken;
  } catch (e) {
    return null;
  }
};

// Raw fetch (not ApiClient): the portal returns 201 on create, which ApiClient
// treats as an error. Returns parsed JSON; throws Error(detail) on non-2xx.
async function apiFetch(path, { method = 'GET', body } = {}) {
  const response = await fetch(`${API_URL}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getAccessToken()}`
    },
    body: body ? JSON.stringify(body) : undefined
  });
  const text = await response.text();
  const data = text ? JSON.parse(text) : null;
  if (!response.ok) {
    throw new Error((data && data.detail) || `HTTP ${response.status}`);
  }
  return data;
}

function CustomPagination(props) {
  const { onPageChange, onRowsPerPageChange, ...rest } = props;
  return (
    <TablePagination
      {...rest}
      onChangePage={onPageChange || props.onChangePage}
      onChangeRowsPerPage={onRowsPerPageChange || props.onChangeRowsPerPage}
    />
  );
}

const sectionStyle = { padding: 16, marginBottom: 16 };
const fieldStyle = { minWidth: 220, marginRight: 12, marginBottom: 12 };

const STATUS_LABEL = { created: 'Created', sent: 'Sent', received: 'Received' };
const STATUS_STYLE = {
  created: { background: '#eeeeee', color: '#555555' },
  sent: { background: '#e3f0ff', color: '#1565c0' },
  received: { background: '#e6f4ea', color: '#2e7d32' }
};
// Forward-only: created → sent → received.
const nextStatus = (s) => (s === 'created' ? 'sent' : s === 'sent' ? 'received' : null);

const statusChip = (s) => (
  <span
    style={{
      ...(STATUS_STYLE[s] || {}),
      padding: '2px 8px',
      borderRadius: 12,
      fontSize: 12,
      fontWeight: 500
    }}
  >
    {STATUS_LABEL[s] || s || '—'}
  </span>
);

function Warehouse() {
  const [tab, setTab] = useState(0);
  const [warehouses, setWarehouses] = useState([]);

  // Create-box state
  const [cbWarehouse, setCbWarehouse] = useState('');
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [boxTracking, setBoxTracking] = useState('');
  const [creating, setCreating] = useState(false);
  const deviceTableRef = useRef();

  // Transfer state
  const [selectedBoxes, setSelectedBoxes] = useState([]);
  const [destination, setDestination] = useState('');
  const [transferring, setTransferring] = useState(false);
  const boxTableRef = useRef();

  // Status-change dialog state
  const [statusBox, setStatusBox] = useState(null);
  const [statusDate, setStatusDate] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);

  useEffect(() => {
    apiFetch('devices/warehouses/')
      .then((data) => setWarehouses(data || []))
      .catch(() => NotyHelper.error('Failed to load warehouses.'));
  }, []);

  function refreshBoxes() {
    if (boxTableRef.current) boxTableRef.current.onQueryChange();
  }

  function submitBox() {
    const deviceIds = selectedDevices.map((d) => d.id);
    if (deviceIds.length === 0) return;
    setCreating(true);
    apiFetch('devices/boxes/', {
      method: 'POST',
      body: { tracking_code: boxTracking.trim(), device_ids: deviceIds }
    })
      .then((box) => {
        NotyHelper.success(`Box ${box && box.tracking_code ? box.tracking_code : ''} created.`);
        setBoxTracking('');
        setSelectedDevices([]);
        if (deviceTableRef.current) deviceTableRef.current.onQueryChange();
        refreshBoxes();
      })
      .catch((e) => NotyHelper.error(e.message))
      .finally(() => setCreating(false));
  }

  function submitTransfer() {
    const boxes = selectedBoxes;
    const trackingCodes = boxes.map((b) => b.tracking_code);
    if (trackingCodes.length === 0 || destination === '') return;
    setTransferring(true);
    apiFetch('devices/boxes/transfer/', {
      method: 'POST',
      body: { tracking_codes: trackingCodes, destination_warehouse: Number(destination) }
    })
      .then(async () => {
        NotyHelper.success(`Boxes transferred: ${trackingCodes.join(', ')}`);
        // Transfer = dispatch: auto-mark as Sent (date = now). Only boxes still
        // in "created" can move to "sent" (forward-only); others are left as-is.
        const at = new Date().toISOString();
        const toSend = boxes.filter((b) => b.status === 'created');
        const results = await Promise.all(
          toSend.map((b) =>
            apiFetch(`devices/boxes/${b.id}/status/`, {
              method: 'PATCH',
              body: { status: 'sent', at }
            })
              .then(() => true)
              .catch(() => false)
          )
        );
        const failed = results.filter((ok) => !ok).length;
        if (failed) {
          NotyHelper.error(
            `Transferred, but ${failed} box(es) could not be marked as Sent. Refresh and retry.`
          );
        }
        setSelectedBoxes([]);
        setDestination('');
        refreshBoxes();
      })
      .catch((e) => NotyHelper.error(e.message))
      .finally(() => setTransferring(false));
  }

  function openStatusDialog(box) {
    setStatusBox(box);
    setStatusDate(moment().format('YYYY-MM-DDTHH:mm'));
  }

  function confirmStatus() {
    if (!statusBox) return;
    const target = nextStatus(statusBox.status);
    if (!target) return;
    setSavingStatus(true);
    apiFetch(`devices/boxes/${statusBox.id}/status/`, {
      method: 'PATCH',
      body: { status: target, at: statusDate ? moment(statusDate).toISOString() : undefined }
    })
      .then(() => {
        NotyHelper.success(`Status changed to ${STATUS_LABEL[target]}.`);
        setStatusBox(null);
        refreshBoxes();
      })
      .catch((e) => NotyHelper.error(e.message))
      .finally(() => setSavingStatus(false));
  }

  const warehouseLabel = (id) => {
    const w = warehouses.find((x) => x.id === id);
    return w ? `${w.code} — ${w.name}` : id ? `#${id}` : '—';
  };

  const warehouseMenu = [
    <MenuItem key="" value="">
      —
    </MenuItem>,
    ...warehouses.map((w) => (
      <MenuItem key={w.id} value={w.id}>
        {w.code} — {w.name}
      </MenuItem>
    ))
  ];

  const fmtDate = (v) => (v ? moment(v).format('DD.MM.YYYY HH:mm') : '');

  const deviceColumns = [
    { title: 'Serial number', field: 'serial_number' },
    { title: 'SKU', field: 'sku' },
    { title: 'Model', field: 'model' },
    { title: 'Created', field: 'created', render: (row) => fmtDate(row.created) }
  ];

  const boxColumns = [
    { title: 'Tracking code', field: 'tracking_code' },
    {
      title: 'Status',
      field: 'status',
      sorting: false,
      editable: 'never',
      render: (row) => statusChip(row.status)
    },
    {
      title: 'Warehouse',
      field: 'warehouse',
      sorting: false,
      editable: 'never',
      render: (row) => warehouseLabel(row.warehouse)
    },
    { title: 'Devices', field: 'device_count', editable: 'never' },
    {
      title: 'Created',
      field: 'created',
      editable: 'never',
      render: (row) => fmtDate(row.created)
    }
  ];

  // Server-side data loader for a paginated/searchable/sortable list endpoint.
  const remoteRows = (basePath, extra = '') => (query) =>
    new Promise((resolve) => {
      let url = `${basePath}?page=${query.page + 1}&page_size=${query.pageSize}${extra}`;
      if (query.search) url += `&search=${encodeURIComponent(query.search)}`;
      if (query.orderBy) {
        const order = (query.orderDirection === 'desc' ? '-' : '') + query.orderBy.field;
        url += `&ordering=${order}`;
      }
      ApiClient.fetchJson(
        url,
        'GET',
        {},
        (response) =>
          resolve({
            data: (response && response.results) || [],
            page: query.page,
            totalCount: (response && response.count) || 0
          }),
        () => resolve({ data: [], page: 0, totalCount: 0 })
      );
    });

  return (
    <div>
      <Header />
      <main style={{ padding: 16 }}>
        <Typography variant="h5" gutterBottom>
          Warehouse
        </Typography>

        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          indicatorColor="primary"
          textColor="primary"
          style={{ marginBottom: 16 }}
        >
          <Tab label="Create box" />
          <Tab label="Transfer" />
        </Tabs>

        {tab === 0 ? (
          <Paper style={sectionStyle}>
            <Typography variant="h6" gutterBottom>
              Create a box
            </Typography>
            <div>
              <TextField
                select
                label="Warehouse"
                variant="outlined"
                size="small"
                style={fieldStyle}
                value={cbWarehouse}
                onChange={(e) => {
                  setCbWarehouse(e.target.value);
                  setSelectedDevices([]);
                }}
              >
                {warehouseMenu}
              </TextField>
              <TextField
                label="Tracking code (optional)"
                variant="outlined"
                size="small"
                style={fieldStyle}
                value={boxTracking}
                onChange={(e) => setBoxTracking(e.target.value)}
              />
            </div>
            {cbWarehouse === '' ? (
              <Typography color="textSecondary">Select a warehouse to list devices.</Typography>
            ) : (
              <Box style={{ marginBottom: 12 }}>
                <MaterialTable
                  key={cbWarehouse}
                  tableRef={deviceTableRef}
                  title="Available devices"
                  columns={deviceColumns}
                  data={remoteRows(
                    'devices/warehouse-devices/',
                    `&warehouse=${cbWarehouse}&available=true`
                  )}
                  components={{ Pagination: CustomPagination }}
                  options={{ pageSize: 10, selection: true, sorting: true }}
                  onSelectionChange={(rows) => setSelectedDevices(rows)}
                />
              </Box>
            )}
            <Button
              variant="contained"
              color="primary"
              disabled={creating || selectedDevices.length === 0}
              onClick={submitBox}
            >
              {creating ? 'Creating…' : `Create box (${selectedDevices.length})`}
            </Button>
          </Paper>
        ) : null}

        {tab === 1 ? (
          <Paper style={sectionStyle}>
            <Typography variant="h6" gutterBottom>
              Send to post / transfer to another warehouse
            </Typography>
            <Box style={{ marginBottom: 12 }}>
              <MaterialTable
                tableRef={boxTableRef}
                title="Boxes"
                columns={boxColumns}
                data={remoteRows('devices/boxes/')}
                components={{ Pagination: CustomPagination }}
                options={{ pageSize: 10, selection: true, sorting: true, actionsColumnIndex: -1 }}
                onSelectionChange={(rows) => setSelectedBoxes(rows)}
                actions={[
                  (row) => ({
                    icon: () => <LocalShippingIcon fontSize="small" />,
                    tooltip:
                      row.status === 'created' ? 'Mark as sent' : 'Mark as received',
                    hidden: row.status === 'received',
                    onClick: (e, rowData) => openStatusDialog(rowData)
                  })
                ]}
                editable={{
                  onRowUpdate: (newData) =>
                    apiFetch(`devices/boxes/${newData.id}/`, {
                      method: 'PATCH',
                      body: { tracking_code: newData.tracking_code }
                    })
                      .then(() => {
                        NotyHelper.success('Tracking code updated.');
                        refreshBoxes();
                      })
                      .catch((e) => {
                        NotyHelper.error(e.message);
                        throw e;
                      })
                }}
              />
            </Box>
            <div>
              <TextField
                select
                label="Destination warehouse"
                variant="outlined"
                size="small"
                style={fieldStyle}
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
              >
                {warehouseMenu}
              </TextField>
            </div>
            <Button
              variant="contained"
              color="primary"
              disabled={transferring || selectedBoxes.length === 0 || destination === ''}
              onClick={submitTransfer}
            >
              {transferring ? 'Transferring…' : `Transfer (${selectedBoxes.length})`}
            </Button>
          </Paper>
        ) : null}

        <Dialog open={Boolean(statusBox)} onClose={() => setStatusBox(null)}>
          <DialogTitle>
            {statusBox && statusBox.status === 'created' ? 'Mark as sent' : 'Mark as received'}
          </DialogTitle>
          <DialogContent>
            <Typography color="textSecondary" gutterBottom>
              {statusBox ? statusBox.tracking_code : ''}
            </Typography>
            <TextField
              label="Date"
              type="datetime-local"
              variant="outlined"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={statusDate}
              onChange={(e) => setStatusDate(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStatusBox(null)} disabled={savingStatus}>
              Cancel
            </Button>
            <Button color="primary" onClick={confirmStatus} disabled={savingStatus}>
              {savingStatus ? 'Saving…' : 'Confirm'}
            </Button>
          </DialogActions>
        </Dialog>
      </main>
    </div>
  );
}

export default Warehouse;
